import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { 
  Phone, 
  MapPin, 
  Mail, 
  Clock,
  Send,
  Loader2,
  Shield
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-vip-black relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 30% 70%, rgba(0, 240, 255, 0.1) 0%, transparent 50%)'
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-neon-cyan uppercase tracking-[0.2em] text-sm mb-4">Get In Touch</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none font-bold italic text-white mb-8">
            Contact <span className="gradient-text">Us</span>
          </h1>
          <p className="text-gray-300 text-base md:text-lg lg:text-xl max-w-2xl mx-auto">
            Have questions about anesthesia or your upcoming procedure? 
            Our team is here to help. Reach out for consultations or general inquiries.
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-24 md:py-32 bg-vip-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">
                Reach Our Team
              </h2>
              
              <div className="space-y-8 mb-12">
                <a 
                  href="tel:8668412730" 
                  data-testid="contact-phone"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-neon-cyan/20 to-neon-pink/20 flex items-center justify-center flex-shrink-0 group-hover:neon-cyan-glow transition-all duration-300">
                    <Phone className="w-6 h-6 text-neon-cyan" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm uppercase tracking-wider mb-1">Phone</p>
                    <p className="text-white text-xl font-heading group-hover:text-neon-cyan transition-colors">866.841.2730</p>
                    <p className="text-gray-500 text-sm mt-1">Main Line</p>
                  </div>
                </a>

                <div 
                  data-testid="contact-main-office"
                  className="flex items-start gap-4"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-neon-pink/20 to-royal-purple/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-neon-pink" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm uppercase tracking-wider mb-1">Main Office</p>
                    <p className="text-white text-xl font-heading">600 Peachtree St NE</p>
                    <p className="text-white text-xl font-heading">Suite 410</p>
                    <p className="text-gray-400 mt-1">Atlanta, GA 30308</p>
                  </div>
                </div>

                <div 
                  data-testid="contact-headquarters"
                  className="flex items-start gap-4"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-royal-purple/20 to-neon-pink/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-royal-purple" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm uppercase tracking-wider mb-1">Headquarters</p>
                    <p className="text-white text-xl font-heading">930 E Old Harmony Rd</p>
                    <p className="text-white text-xl font-heading">Suite C</p>
                    <p className="text-gray-400 mt-1">Newark, DE 19713</p>
                  </div>
                </div>

                <div 
                  data-testid="contact-email"
                  className="flex items-start gap-4"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-royal-purple/20 to-neon-cyan/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-royal-purple" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm uppercase tracking-wider mb-1">Email</p>
                    <p className="text-white text-lg font-heading break-all">info@surgicalcarecenter.online</p>
                  </div>
                </div>

                <div 
                  data-testid="contact-hours"
                  className="flex items-start gap-4"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-neon-cyan/20 to-royal-purple/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-neon-cyan" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm uppercase tracking-wider mb-1">Hours of Operation</p>
                    <p className="text-white">Monday - Friday: 9AM - 5PM</p>
                    <p className="text-white">Saturday - Sunday: 11AM - 4PM</p>
                  </div>
                </div>

                <div 
                  data-testid="contact-npi"
                  className="flex items-start gap-4"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-neon-pink/20 to-neon-cyan/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-neon-pink" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm uppercase tracking-wider mb-1">NPI Number</p>
                    <a 
                      href="https://npiregistry.cms.hhs.gov/provider-view/1952213472" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-white text-xl font-heading hover:text-neon-cyan transition-colors"
                    >
                      1952213472
                    </a>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div 
                data-testid="contact-map"
                className="aspect-video rounded-2xl overflow-hidden border border-white/10 relative"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3315.5544166827926!2d-84.38716532432444!3d33.77186997332493!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88f5040c8fabb1c1%3A0x8b3f60d51fee6f22!2s600%20Peachtree%20St%20NE%2C%20Atlanta%2C%20GA%2030308!5e0!3m2!1sen!2sus!4v1700000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(100%) brightness(0.4) contrast(1.2)' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Surgery Center Location"
                />
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-vip-paper via-transparent to-transparent" />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="glass rounded-2xl p-8 md:p-10 border border-white/10">
                <h2 className="font-heading text-2xl md:text-3xl text-white mb-2">
                  Send a Message
                </h2>
                <p className="text-gray-400 mb-8">
                  All inquiries are handled with complete confidentiality.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                      Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      data-testid="contact-name-input"
                      className="bg-white/5 border-0 border-b border-white/20 rounded-none px-0 py-4 focus-visible:ring-0 focus-visible:border-neon-cyan text-lg placeholder:text-white/30 text-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                      Email *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      data-testid="contact-email-input"
                      className="bg-white/5 border-0 border-b border-white/20 rounded-none px-0 py-4 focus-visible:ring-0 focus-visible:border-neon-cyan text-lg placeholder:text-white/30 text-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                      Phone
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 000-0000"
                      data-testid="contact-phone-input"
                      className="bg-white/5 border-0 border-b border-white/20 rounded-none px-0 py-4 focus-visible:ring-0 focus-visible:border-neon-cyan text-lg placeholder:text-white/30 text-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="How can we assist you?"
                      rows={5}
                      data-testid="contact-message-input"
                      className="bg-white/5 border-0 border-b border-white/20 rounded-none px-0 py-4 focus-visible:ring-0 focus-visible:border-neon-cyan text-lg placeholder:text-white/30 text-white resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="w-full rounded-full bg-white text-black hover:bg-neon-cyan hover:text-black font-bold uppercase tracking-widest py-6 text-base shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Notice */}
      <section className="py-16 bg-vip-black border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-gray-500 text-sm">
            Your privacy is our priority. All communications are encrypted and handled 
            with the strictest confidentiality. We never share your information with third parties.
          </p>
        </div>
      </section>
    </div>
  );
}
